import api from "../../services/baseapi";

export type ApiPriority = "low" | "medium" | "high" | "urgent";

export interface ApiAssignee {
  id: number;
  username: string;
}

export interface ApiTask {
  id: string;
  project_id: string;
  status_id: string | null;
  status_name: string | null;
  status_color: string | null;
  title: string;
  description: string | null;
  priority: ApiPriority;
  is_milestone: boolean;
  due_date: string | null;
  linked_tool: string | null;
  position: number;
  assignee: ApiAssignee | null;
  created_by: string;
  created_by_username: string;
  created_at: string;
  modified_at: string | null;
  comments_count: number;
  attachments_count: number;
}

export interface ApiTaskWithDeadline extends ApiTask {
  days_remaining: number | null;
  is_overdue: boolean;
  is_due_soon: boolean;
}

export interface ApiColumn {
  id: string;
  project_id: string;
  name: string;
  color: string;
  position: number;
  is_default: boolean;
  created_at: string;
  modified_at: string | null;
  tasks: ApiTask[];
}

export interface ApiBoardResponse {
  project_id: string;
  columns: ApiColumn[];
}

export interface ApiComment {
  id: string;
  task_id: string;
  user_id: number;
  username: string;
  content: string;
  created_at: string;
  modified_at: string | null;
}

export interface ApiCommentsResponse {
  total: number;
  skip: number;
  limit: number;
  comments: ApiComment[];
}

export interface ApiMilestonesSummary {
  on_track: number;
  overdue: number;
  due_this_week: number;
}

export interface ApiMilestonesResponse {
  summary: ApiMilestonesSummary;
  milestones: ApiTaskWithDeadline[];
}

export interface ApiAttachment {
  id: string;
  task_id: string;
  file_name: string;
  url: string;
  mime_type: string | null;
  file_size: number | null;
  uploaded_by: number;
  created_at: string;
}

export interface ApiNotification {
  id: string | null;
  type: "assigned" | "comment" | "due_in_48h" | "overdue";
  project_id: string;
  task_id: string;
  task_title: string;
  comment_id: string | null;
  actor: { id: number; username: string } | null;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface ApiNotificationsResponse {
  total: number;
  unread: number;
  notifications: ApiNotification[];
}

export interface ProjectMember {
  id: number;
  username: string;
}

// ── Board ────────────────────────────────────────────────────────────────────

export const getBoard = async (
  projectId: string,
  params?: {
    assignee_user_id?: number;
    priority?: ApiPriority;
    linked_tool?: string;
  },
): Promise<ApiBoardResponse> => {
  const { data } = await api.get(`/projects/${projectId}/hub/board`, {
    params,
  });
  return data;
};

// ── Columns ──────────────────────────────────────────────────────────────────

export const reorderColumns = async (
  projectId: string,
  items: { id: string; position: number }[],
): Promise<void> => {
  await api.patch(`/projects/${projectId}/hub/statuses/reorder`, { items });
};

// ── Tasks ────────────────────────────────────────────────────────────────────

export const createTask = async (
  projectId: string,
  payload: {
    title: string;
    description?: string;
    priority?: ApiPriority;
    is_milestone?: boolean;
    due_date?: string;
    linked_tool?: string;
    status_id?: string;
    assignee_user_id?: number;
  },
): Promise<ApiTask> => {
  const { data } = await api.post(`/projects/${projectId}/hub/tasks`, payload);
  return data;
};

export const updateTask = async (
  projectId: string,
  taskId: string,
  payload: {
    title?: string;
    description?: string;
    priority?: ApiPriority;
    is_milestone?: boolean;
    due_date?: string | null;
    linked_tool?: string | null;
    status_id?: string;
    assignee_user_id?: number | null;
  },
): Promise<ApiTask> => {
  const { data } = await api.patch(
    `/projects/${projectId}/hub/tasks/${taskId}`,
    payload,
  );
  return data;
};

export const moveTask = async (
  projectId: string,
  taskId: string,
  statusId: string,
  position: number,
): Promise<ApiTask> => {
  const { data } = await api.patch(
    `/projects/${projectId}/hub/tasks/${taskId}/move`,
    { status_id: statusId, position },
  );
  return data;
};

export const deleteTask = async (
  projectId: string,
  taskId: string,
): Promise<void> => {
  await api.delete(`/projects/${projectId}/hub/tasks/${taskId}`);
};

// ── Milestones / Deadlines ───────────────────────────────────────────────────

export const getMilestones = async (
  projectId: string,
  params?: {
    assignee_user_id?: number;
    priority?: ApiPriority;
    linked_tool?: string;
  },
): Promise<ApiMilestonesResponse> => {
  const { data } = await api.get(
    `/projects/${projectId}/hub/tasks/milestones`,
    { params },
  );
  return data;
};

export const getDeadlines = async (
  projectId: string,
  params?: {
    date_from?: string;
    date_to?: string;
    assignee_user_id?: number;
    priority?: ApiPriority;
    linked_tool?: string;
  },
): Promise<ApiTaskWithDeadline[]> => {
  const { data } = await api.get(`/projects/${projectId}/hub/tasks/deadlines`, {
    params,
  });
  return data;
};

// ── Comments ─────────────────────────────────────────────────────────────────

export const getTaskComments = async (
  projectId: string,
  taskId: string,
  skip = 0,
  limit = 50,
): Promise<ApiCommentsResponse> => {
  const { data } = await api.get(
    `/projects/${projectId}/hub/tasks/${taskId}/comments`,
    { params: { skip, limit } },
  );
  return data;
};

export const postComment = async (
  projectId: string,
  taskId: string,
  content: string,
): Promise<ApiComment> => {
  const { data } = await api.post(
    `/projects/${projectId}/hub/tasks/${taskId}/comments`,
    { content },
  );
  return data;
};

// ── Attachments ──────────────────────────────────────────────────────────────

export const getAttachments = async (
  projectId: string,
  taskId: string,
): Promise<ApiAttachment[]> => {
  const { data } = await api.get(
    `/projects/${projectId}/hub/tasks/${taskId}/attachments`,
  );
  return Array.isArray(data) ? data : [];
};

export const addAttachment = async (
  projectId: string,
  taskId: string,
  payload: {
    file_name: string;
    url: string;
    mime_type?: string;
    file_size?: number;
  },
): Promise<ApiAttachment> => {
  const { data } = await api.post(
    `/projects/${projectId}/hub/tasks/${taskId}/attachments`,
    payload,
  );
  return data;
};

export const deleteAttachment = async (
  projectId: string,
  taskId: string,
  attachmentId: string,
): Promise<void> => {
  await api.delete(
    `/projects/${projectId}/hub/tasks/${taskId}/attachments/${attachmentId}`,
  );
};

// ── Assignees ────────────────────────────────────────────────────────────────
// Role-scoped: Admin/PM get all project members; other roles get only themselves.

export const getAssignees = async (
  projectId: string,
): Promise<ProjectMember[]> => {
  const { data } = await api.get(`/projects/${projectId}/hub/assignees`);
  return Array.isArray(data) ? data : [];
};

// ── Notifications (cross-project) ────────────────────────────────────────────

export const getNotificationsUnreadCount = async (): Promise<{
  unread: number;
}> => {
  const { data } = await api.get("/me/project-hub/notifications/unread-count");
  return data;
};

export const getNotifications = async (params?: {
  only_unread?: boolean;
  skip?: number;
  limit?: number;
}): Promise<ApiNotificationsResponse> => {
  const { data } = await api.get("/me/project-hub/notifications", { params });
  return data;
};

export const markNotificationsRead = async (
  notificationIds?: string[],
): Promise<{ marked_read: number }> => {
  const body = notificationIds ? { notification_ids: notificationIds } : {};
  const { data } = await api.patch("/me/project-hub/notifications/read", body);
  return data;
};
